<template>
    <div
        ref="webInfoPanel"
        v-if="visible && currentObject"
        class="web-information-panel shadow show"
        :class="{ 'has-search': hasSearch, 'one-object-only': objects.length == 1 }"
    >
        <div class="panel-header">
            <h3 class="panel-header-title crop">
                {{ currentObject.layerTitle }}
            </h3>
            <!-- Close icon -->
            <icon-close
                :id="'web-information-close'"
                @close="$_onCancel"
                class="d-inline-block float-right"
            >
            </icon-close>
        </div>
        <div class="panel-content">
            <Pagination
                v-if="objects.length > 1"
                :features="objects"
                :indexStart="selectedIndex"
                @indexChanged="$_onIndexChanged($event)"
            >
            </Pagination>
            <div class="object-content">
                <object-detail :displayObject="currentObject" />
            </div>
            <!-- Buttons -->
            <div v-if="isFeedBack" class="object-detail-buttons">
                <!-- FeedBack delete -->
                <base-button
                    id="web_object_detail_delete"
                    @click="$_deleteFeedbackModel"
                    :text="$t('btnDeleteLabel')"
                >
                </base-button>
            </div>
        </div>
    </div>
</template>

<script>
    import { mapState, mapActions, mapGetters } from 'vuex';

    import {
        CONTENT_TYPE_ID,
        DEFAULT_ANIMATION_MS,
        NEDGLOBE_VIEWER,
        INTERFACE_PANEL_TRIGGERS,
        PANEL_WIDTHS
    } from '../../../../constants';
    import { isSmallDevice } from '../../../../services/utilities/devices';
    import IconClose from '../../../IconClose.vue';
    import Pagination from '../../../Pagination.vue';
    import ObjectDetail from '../../../geo/information/feature/ObjectDetail';
    import { objectData } from '../../../../services/selections';
    import BaseButton from '../../../BaseComponents/base-button.vue';

    export default {
        components: {
            'icon-close': IconClose,
            Pagination: Pagination,
            'object-detail': ObjectDetail,
            'base-button': BaseButton
        },
        data() {
            return {
                selectedIndex: 0,
                feature: null
            };
        },
        watch: {
            objects: function (newVal) {
                // reset selectedIndex when new list of objects
                this.selectedIndex = 0;
            },
            isVisibleAndHasFeature(value) {
                if (value) {
                    // Wait a little bit to draw the panel and we can get the height
                    this.$nextTick(() => {
                        const windowHeight = this.$refs.webInfoPanel.clientHeight;
                        this.$store.commit('SET_HEIGHT_MENU_WEB_INFORMATION', windowHeight);

                        const isSmallDeviceValue = isSmallDevice();
                        const margin = 100;
                        const popupWidth = PANEL_WIDTHS.WEB_INFORMATION;
                        const marginRight = isSmallDeviceValue
                            ? margin
                            : popupWidth + margin;

                        this.$store.dispatch('zoomToFeatures', {
                            mapName: 'main',
                            features: [this.feature],
                            zoomWhenOutOfView: true,
                            options: {
                                padding: [margin, marginRight, margin, margin],
                                duration: DEFAULT_ANIMATION_MS
                            }
                        });
                    });
                }
            },
            currentObject: {
                handler: function (newVal) {
                    if (newVal) {
                        const { computedStyles } = this.$store.getters.getLayerById(
                            newVal.layerId
                        ).extended;
                        const features = this.objects
                            .filter(({ layerId }) => layerId === newVal.layerId)
                            .map(({ feature }) => feature);

                        this.$store.commit('SHOW_MAP_FEATURE', {
                            mapName: 'main',
                            customLayerName:
                                NEDGLOBE_VIEWER.customLayers.printable.selections,
                            features,
                            computedStyles,
                            styleType: NEDGLOBE_VIEWER.styleDisplayTypes.selection,
                            reset: true
                        });

                        this.$store.commit('SHOW_MAP_FEATURE', {
                            mapName: 'main',
                            customLayerName:
                                NEDGLOBE_VIEWER.customLayers.printable.selection,
                            feature: newVal.feature,
                            computedStyles,
                            styleType: NEDGLOBE_VIEWER.styleDisplayTypes.selected,
                            reset: true
                        });
                        this.feature = newVal.feature;
                    } else {
                        this.$_onCancel();
                    }
                },
                immediate: false
            }
        },
        computed: {
            ...mapState(['uiElements']),
            ...mapGetters(['getLocale', 'getWebTheme']),
            currentObject: function () {
                return this.objects?.[this.selectedIndex];
            },
            isVisibleAndHasFeature() {
                return this.visible && this.feature;
            },
            isFeedBack: function () {
                return this.currentObject.layer.type === 'feedback';
            },
            webInformationPanel: function () {
                return this.uiElements[INTERFACE_PANEL_TRIGGERS.WEB_INFORMATION];
            },
            hasSearch() {
                return this.getWebTheme?.data?.showSearch;
            },
            visible() {
                return this.webInformationPanel.visible;
            },
            featuresData() {
                return this.webInformationPanel.data;
            },
            featuresInLayers: function () {
                if (!this.featuresData?.results) {
                    return null;
                }
                return this.featuresData.results.map(({ features, layer }) => ({
                    features,
                    layer
                }));
            },
            objects: function () {
                if (!this.featuresInLayers) {
                    return null;
                }
                const flattendFeatures = this.featuresInLayers
                    .map(({ features, layer }) =>
                        objectData(
                            features,
                            layer,
                            false,
                            false,
                            this.getLocale
                        ).map((item) => ({
                            ...item,
                            layer
                        }))
                    )
                    .map((item) =>
                        item.map(({ feature, layer, info }) => {
                            const objectinfo = Object.values(info);
                            return {
                                layerId: layer.id,
                                layerTitle: layer.data.title,
                                layer: layer,
                                info: objectinfo.filter(({ popup }) => popup),
                                feature
                            };
                        })
                    )
                    .flatMap((info) => info);
                // filter personalData
                flattendFeatures.forEach(f => {
                    f.info = f.info.filter(i => !i.personalData);
                });
                return flattendFeatures;
            }
        },
        methods: {
            ...mapActions(['togglePanel']),
            $_onIndexChanged(index) {
                this.selectedIndex = index;
                this.$emit('open', this.currentObject);
            },
            $_onCancel() {
                this.$store.commit('RESET_MAP_FEATURES', {
                    mapName: null,
                    customLayerName:
                        NEDGLOBE_VIEWER.customLayers.printable.selection
                });
                this.$store.commit('RESET_MAP_FEATURES', {
                    mapName: null,
                    customLayerName: NEDGLOBE_VIEWER.customLayers.printable.selected
                });
                this.togglePanel({
                    panelName: INTERFACE_PANEL_TRIGGERS.WEB_INFORMATION,
                    visible: false,
                    data: null
                });
                this.$store.commit('SET_HEIGHT_MENU_WEB_INFORMATION', 0);
            },
            $_deleteFeedbackModel() {
                const feature = this.currentObject.feature;
                const layer = this.currentObject.layer;
                const panelName =
                    layer.extended.contentTypeId === CONTENT_TYPE_ID.FEEDBACK
                        ? 'feedbackEdit'
                        : 'measureEdit';
                this.$store.dispatch('togglePanel', {
                    panelName,
                    visible: true,
                    data: { layer, features: [feature] },
                    mode: 'delete'
                });
                this.$_onCancel();
            }
        }
    };
</script>

<style lang="scss" scoped>
    .web-information-panel {
        @include margin-top-navbar;
        position: absolute;
        display: none;
        flex-direction: column;
        max-height: 100%;
        min-width: 250px;
        z-index: 22;
        background-color: #fff;
        transition: all 0.3s ease-in-out;

        .large-navbar & {
            max-height: calc(100% - var(--paneltop));
        }

        &.show {
            display: flex;
            right: 0;
            left: auto;
            width: $default-panel-width;

            &.one-object-only {
                max-width: $default-panel-width;
                width: initial;
            }
        }

        .panel-header-title {
            padding-left: 3px;
        }

        .panel-content {
            display: flex;
            flex-direction: column;
            overflow: auto;
            padding: 0px;

            & .object-detail-paging {
                padding: 5px;
            }
        }

        .object-content {
            overflow: auto;
            flex: 1;
            padding: 0px 5px 5px 5px;
        }

        .object-detail-buttons {
            margin-top: 10px;
            display: flex;
            justify-content: flex-end;
            padding: 0px 5px 5px 5px;
        }
    }

    @media (max-width: 767px) {
        .web-information-panel.show {
            min-width: auto;
            width: 100%;

            &.one-object-only {
                max-width: initial;
                width: 100%;
            }

            &.has-search {
                top: var(--paneltop);
                max-height: calc(100% - var(--paneltop));
            }
        }
    }
</style>
